import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { robotoRegularBase64, robotoBoldBase64 } from '../ecc/roboto';
import {
  FileText, Calendar, Plus, Edit2, Trash2, Download, Save, X,
  ChevronLeft, ChevronRight, Users, Cloud, Wind, Thermometer, Loader2, Clock, Eye,
  Search, Filter, SortAsc, SortDesc, CheckCircle, AlertCircle, FileEdit, BarChart3, RefreshCw
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const DailyReport = () => {
  // Project state
  const [selectedProject, setSelectedProject] = useState('');
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  // State
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentReport, setCurrentReport] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    weather: '',
    wind_speed: '',
    temperature: '',
    omega_supervisor: null,
    customer_representative: '',
    notes: '',
    omega_supervisor_signature: '',
    omega_supervisor_signature_date: '',
    customer_signature: '',
    customer_signature_date: '',
    status: 'draft',
    entries: [],
    additional_works: []
  });

  const [allPeople, setAllPeople] = useState([]);

  // Filter states
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showFilters, setShowFilters] = useState(true);
  const [companyLogo, setCompanyLogo] = useState(null);

  // Load projects and people on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoadingProjects(true);
        const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };

        const [projectsResponse, usersResponse, companyResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/projects/`, { headers }),
          axios.get(`${API_BASE_URL}/api/users/`, { headers }),
          axios.get(`${API_BASE_URL}/company/settings/`, { headers }).catch(() => ({ data: null }))
        ]);

        setProjects(projectsResponse.data);
        setAllPeople(usersResponse.data);

        // Get company logo if available
        if (companyResponse.data && companyResponse.data.company_logo) {
          // Logo URL might be relative, prefix with API_BASE_URL if needed
          let logoUrl = companyResponse.data.company_logo;
          if (logoUrl && !logoUrl.startsWith('http')) {
            logoUrl = `${API_BASE_URL}${logoUrl}`;
          }
          setCompanyLogo(logoUrl);
        }

        // Auto-select first project if available
        if (projectsResponse.data.length > 0) {
          setSelectedProject(projectsResponse.data[0].id.toString());
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setLoadingProjects(false);
      }
    };
    loadInitialData();
  }, []);

  // Load reports for selected project
  const loadReports = useCallback(async () => {
    if (!selectedProject) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/daily-reports/?project=${selectedProject}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      );
      setReports(response.data);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // Generate report from tasks
  const generateFromTasks = async () => {
    if (!selectedProject) return;

    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(
        `${API_BASE_URL}/api/daily-reports/generate_from_tasks/?project=${selectedProject}&date=${dateStr}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      );

      if (response.data.exists) {
        // Report already exists, load it
        setCurrentReport(response.data.report);
        setFormData({
          ...response.data.report,
          entries: response.data.report.entries || [],
          additional_works: response.data.report.additional_works || []
        });
        setIsEditing(true);
      } else {
        // New report from template
        setCurrentReport(null);
        setFormData({
          ...response.data.template,
          entries: response.data.template.entries || [],
          additional_works: response.data.template.additional_works || []
        });
        setIsEditing(false);
      }
      setViewOnly(false);
      setShowReportForm(true);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Generate report from timesheets
  const generateFromTimesheets = async () => {
    if (!selectedProject) return;

    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(
        `${API_BASE_URL}/api/daily-reports/generate_from_timesheets/?project=${selectedProject}&date=${dateStr}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      );

      if (response.data.exists) {
        // Report already exists, load it
        setCurrentReport(response.data.report);
        setFormData({
          ...response.data.report,
          entries: response.data.report.entries || [],
          additional_works: response.data.report.additional_works || []
        });
        setIsEditing(true);
      } else {
        // New report from template (tasks empty - to be filled manually)
        setCurrentReport(null);
        setFormData({
          ...response.data.template,
          entries: response.data.template.entries || [],
          additional_works: response.data.template.additional_works || []
        });
        setIsEditing(false);
      }
      setViewOnly(false);
      setShowReportForm(true);
    } catch (error) {
      console.error('Error generating report from timesheets:', error);
      alert('Error generating report: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Save report
  const saveReport = async () => {
    setSaving(true);
    try {
      const payload = {
        project: selectedProject,
        date: format(selectedDate, 'yyyy-MM-dd'),
        weather: formData.weather,
        wind_speed: formData.wind_speed,
        temperature: formData.temperature,
        omega_supervisor: formData.omega_supervisor,
        customer_representative: formData.customer_representative,
        notes: formData.notes,
        omega_supervisor_signature: formData.omega_supervisor_signature,
        omega_supervisor_signature_date: formData.omega_supervisor_signature_date || null,
        customer_signature: formData.customer_signature,
        customer_signature_date: formData.customer_signature_date || null,
        status: formData.status,
        entries: formData.entries.map((entry, idx) => ({
          technician: entry.technician,
          initials: entry.initials,
          tasks: entry.tasks,
          start_time: entry.start_time,
          end_time: entry.end_time,
          installation_hours: parseFloat(entry.installation_hours) || 0,
          admin_hours: parseFloat(entry.admin_hours) || 0,
          extra_work_hours: parseFloat(entry.extra_work_hours) || 0,
          waiting_weather_hours: parseFloat(entry.waiting_weather_hours) || 0,
          ncr_waiting_hours: parseFloat(entry.ncr_waiting_hours) || 0,
          break_time: parseFloat(entry.break_time) || 0.75,
          order: idx
        })),
        additional_works: formData.additional_works.map((work, idx) => ({
          description: work.description,
          ncr_reference: work.ncr_reference,
          hours: parseFloat(work.hours) || 0,
          order: idx,
          technicians: (work.technicians || []).map((tech) => ({
            technician: tech.technician,
            start_time: tech.start_time,
            stop_time: tech.stop_time,
            break_time: parseFloat(tech.break_time) || 0
          }))
        }))
      };

      let response;
      if (currentReport?.id) {
        response = await axios.put(
          `${API_BASE_URL}/api/daily-reports/${currentReport.id}/`,
          payload,
          { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/api/daily-reports/`,
          payload,
          { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
        );
      }

      setCurrentReport(response.data);
      setIsEditing(true);
      loadReports();
      alert('Report saved successfully!');
    } catch (error) {
      console.error('Error saving report:', error);
      alert('Error saving report: ' + JSON.stringify(error.response?.data || error.message));
    } finally {
      setSaving(false);
    }
  };

  // Delete report
  const deleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/api/daily-reports/${reportId}/`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      );
      loadReports();
      if (currentReport?.id === reportId) {
        setShowReportForm(false);
        setCurrentReport(null);
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Error deleting report');
    }
  };

  // Load existing report
  const loadReport = async (report, isViewOnly = false) => {
    setCurrentReport(report);
    setFormData({
      ...report,
      entries: report.entries || [],
      additional_works: report.additional_works || []
    });
    setSelectedDate(parseISO(report.date));
    setIsEditing(true);
    setViewOnly(isViewOnly);
    setShowReportForm(true);
  };

  // View report (read-only)
  const viewReport = (report) => {
    loadReport(report, true);
  };

  // Add new entry row
  const addEntry = () => {
    setFormData(prev => ({
      ...prev,
      entries: [...prev.entries, {
        technician: null,
        technician_name: '',
        initials: '',
        tasks: '',
        start_time: '08:00',
        end_time: '18:00',
        installation_hours: 9.25,
        admin_hours: 0,
        extra_work_hours: 0,
        waiting_weather_hours: 0,
        ncr_waiting_hours: 0,
        break_time: 0.75,
        order: prev.entries.length
      }]
    }));
  };

  // Remove entry
  const removeEntry = (index) => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.filter((_, i) => i !== index)
    }));
  };

  // Calculate work hours from start, stop and break
  const calculateWorkHours = (startTime, endTime, breakTime) => {
    if (!startTime || !endTime) return 0;
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    const totalMinutes = endMinutes - startMinutes;
    const totalHours = totalMinutes / 60;
    return Math.max(0, totalHours - (parseFloat(breakTime) || 0));
  };

  // Update entry
  const updateEntry = (index, field, value) => {
    setFormData(prev => {
      const newEntries = prev.entries.map((entry, i) => {
        if (i !== index) return entry;
        const updatedEntry = { ...entry, [field]: value };

        // Auto-calculate work hours when time or break changes
        if (['start_time', 'end_time', 'break_time'].includes(field)) {
          const workHours = calculateWorkHours(
            updatedEntry.start_time,
            updatedEntry.end_time,
            updatedEntry.break_time
          );
          updatedEntry.installation_hours = workHours.toFixed(2);
        }

        return updatedEntry;
      });
      return { ...prev, entries: newEntries };
    });
  };

  // Recalculate work hours for all entries
  const recalculateAllWorkHours = () => {
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.map(entry => ({
        ...entry,
        installation_hours: calculateWorkHours(
          entry.start_time,
          entry.end_time,
          entry.break_time
        ).toFixed(2)
      }))
    }));
  };

  // Add additional work
  const addAdditionalWork = () => {
    setFormData(prev => ({
      ...prev,
      additional_works: [...prev.additional_works, {
        description: '',
        ncr_reference: '',
        hours: 0,
        order: prev.additional_works.length,
        technicians: []
      }]
    }));
  };

  // Remove additional work
  const removeAdditionalWork = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_works: prev.additional_works.filter((_, i) => i !== index)
    }));
  };

  // Update additional work
  const updateAdditionalWork = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      additional_works: prev.additional_works.map((work, i) =>
        i === index ? { ...work, [field]: value } : work
      )
    }));
  };

  // Add technician to additional work
  const addAdditionalWorkTechnician = (workIndex) => {
    setFormData(prev => ({
      ...prev,
      additional_works: prev.additional_works.map((work, i) =>
        i === workIndex ? {
          ...work,
          technicians: [...(work.technicians || []), {
            technician: '',
            start_time: '',
            stop_time: '',
            break_time: 0
          }]
        } : work
      )
    }));
  };

  // Calculate total hours for additional work technicians
  const calculateAdditionalWorkHours = (technicians) => {
    if (!technicians || technicians.length === 0) return 0;
    return technicians.reduce((sum, tech) => {
      const hours = calculateWorkHours(tech.start_time, tech.stop_time, tech.break_time);
      return sum + hours;
    }, 0);
  };

  // Remove technician from additional work
  const removeAdditionalWorkTechnician = (workIndex, techIndex) => {
    setFormData(prev => {
      const newWorks = prev.additional_works.map((work, i) => {
        if (i !== workIndex) return work;
        const newTechnicians = work.technicians.filter((_, ti) => ti !== techIndex);
        return {
          ...work,
          technicians: newTechnicians,
          hours: calculateAdditionalWorkHours(newTechnicians).toFixed(2)
        };
      });
      return { ...prev, additional_works: newWorks };
    });
  };

  // Update technician in additional work
  const updateAdditionalWorkTechnician = (workIndex, techIndex, field, value) => {
    setFormData(prev => {
      const newWorks = prev.additional_works.map((work, i) => {
        if (i !== workIndex) return work;
        const newTechnicians = work.technicians.map((tech, ti) =>
          ti === techIndex ? { ...tech, [field]: value } : tech
        );
        // Auto-calculate hours when time or break changes
        const newHours = ['start_time', 'stop_time', 'break_time'].includes(field)
          ? calculateAdditionalWorkHours(newTechnicians).toFixed(2)
          : work.hours;
        return {
          ...work,
          technicians: newTechnicians,
          hours: newHours
        };
      });
      return { ...prev, additional_works: newWorks };
    });
  };

  // Calculate totals
  const calculateTotals = () => {
    const entries = formData.entries || [];
    return {
      installation: entries.reduce((sum, e) => sum + (parseFloat(e.installation_hours) || 0), 0),
      admin: entries.reduce((sum, e) => sum + (parseFloat(e.admin_hours) || 0), 0),
      extra: entries.reduce((sum, e) => sum + (parseFloat(e.extra_work_hours) || 0), 0),
      waitingWeather: entries.reduce((sum, e) => sum + (parseFloat(e.waiting_weather_hours) || 0), 0),
      ncrWaiting: entries.reduce((sum, e) => sum + (parseFloat(e.ncr_waiting_hours) || 0), 0),
      breakTime: entries.reduce((sum, e) => sum + (parseFloat(e.break_time) || 0), 0),
      additionalHours: (formData.additional_works || []).reduce((sum, w) => sum + (parseFloat(w.hours) || 0), 0)
    };
  };

  // Filter and sort reports
  const filteredReports = useMemo(() => {
    let result = [...reports];

    // Filter by status
    if (filterStatus !== 'all') {
      result = result.filter(r => r.status === filterStatus);
    }

    // Filter by date range
    if (filterDateFrom) {
      result = result.filter(r => r.date >= filterDateFrom);
    }
    if (filterDateTo) {
      result = result.filter(r => r.date <= filterDateTo);
    }

    // Search query (search in notes, supervisor name)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(r => {
        const supervisorName = r.omega_supervisor
          ? allPeople.find(p => p.id === r.omega_supervisor)
          : null;
        const supervisorFullName = supervisorName
          ? `${supervisorName.first_name} ${supervisorName.last_name}`.toLowerCase()
          : '';
        return (
          r.notes?.toLowerCase().includes(query) ||
          r.customer_representative?.toLowerCase().includes(query) ||
          supervisorFullName.includes(query) ||
          format(parseISO(r.date), 'dd.MM.yyyy').includes(query)
        );
      });
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'date':
          comparison = a.date.localeCompare(b.date);
          break;
        case 'workers':
          comparison = (a.workers_on_site || 0) - (b.workers_on_site || 0);
          break;
        case 'hours':
          comparison = (a.total_normal_hours || 0) - (b.total_normal_hours || 0);
          break;
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '');
          break;
        default:
          comparison = 0;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [reports, filterStatus, filterDateFrom, filterDateTo, searchQuery, sortField, sortDirection, allPeople]);

  // Statistics
  const reportStats = useMemo(() => {
    const total = reports.length;
    const draft = reports.filter(r => r.status === 'draft').length;
    const pending = reports.filter(r => r.status === 'pending_approval').length;
    const approved = reports.filter(r => r.status === 'approved').length;
    const totalHours = reports.reduce((sum, r) => sum + (r.total_normal_hours || 0), 0);
    const totalWorkers = reports.reduce((sum, r) => sum + (r.workers_on_site || 0), 0);
    return { total, draft, pending, approved, totalHours, totalWorkers };
  }, [reports]);

  // Clear filters
  const clearFilters = () => {
    setFilterStatus('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setSearchQuery('');
  };

  // Toggle sort
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Export to PDF
  const exportToPDF = async () => {
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    let yPos = margin;

    // Add Polish font support
    doc.setLanguage('pl');
    doc.addFileToVFS('Roboto-Regular.ttf', robotoRegularBase64);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.addFileToVFS('Roboto-Bold.ttf', robotoBoldBase64);
    doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
    doc.setFont('Roboto', 'normal'); // Set default font to Roboto

    // Colors
    const headerBg = [66, 66, 66];
    const greenBg = [200, 230, 201];
    const orangeBg = [255, 224, 178];
    const grayBg = [240, 240, 240];

    // Title section with logo
    doc.setFillColor(...headerBg);
    doc.rect(margin, yPos, pageWidth - 2 * margin, 12, 'F');

    // Add company logo if available
    let logoAdded = false;
    if (companyLogo) {
      try {
        // Fetch logo as blob to avoid CORS issues
        const logoResponse = await fetch(companyLogo, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (logoResponse.ok) {
          const logoBlob = await logoResponse.blob();
          const logoDataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
          });

          // Add logo on the left side of header (max height 10mm, maintain aspect ratio)
          const logoImg = new Image();
          await new Promise((resolve, reject) => {
            logoImg.onload = resolve;
            logoImg.onerror = reject;
            logoImg.src = logoDataUrl;
          });

          const logoHeight = 10;
          const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
          doc.addImage(logoDataUrl, 'PNG', margin + 2, yPos + 1, Math.min(logoWidth, 40), logoHeight);
          logoAdded = true;

          // Title text after logo
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(14);
          doc.setFont('Roboto', 'bold');
          doc.text('Daily Report Sheet', margin + Math.min(logoWidth, 40) + 8, yPos + 8);
        }
      } catch (e) {
        console.error('Error loading logo:', e);
      }
    }

    if (!logoAdded) {
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('Roboto', 'bold');
      doc.text('Daily Report Sheet', margin + 5, yPos + 8);
    }

    doc.setFontSize(9);
    doc.setFont('Roboto', 'normal');
    doc.text('Rev. 2', pageWidth - margin - 15, yPos + 8);
    yPos += 14;

    // Project info row
    doc.setFillColor(...grayBg);
    doc.rect(margin, yPos, pageWidth - 2 * margin, 10, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);

    const projectInfo = project || {};
    const infoColWidth = (pageWidth - 2 * margin) / 4;

    doc.setFont('Roboto', 'bold');
    doc.text('Project No:', margin + 2, yPos + 6);
    doc.setFont('Roboto', 'normal');
    doc.text(projectInfo.project_number || '', margin + 25, yPos + 6);

    doc.setFont('Roboto', 'bold');
    doc.text('Project Name:', margin + infoColWidth, yPos + 6);
    doc.setFont('Roboto', 'normal');
    doc.text(projectInfo.name || '', margin + infoColWidth + 28, yPos + 6);

    doc.setFont('Roboto', 'bold');
    doc.text('Date:', margin + 2 * infoColWidth, yPos + 6);
    doc.setFont('Roboto', 'normal');
    doc.text(format(selectedDate, 'dd.MM.yyyy'), margin + 2 * infoColWidth + 15, yPos + 6);

    doc.setFont('Roboto', 'bold');
    doc.text('Workers on site:', margin + 3 * infoColWidth, yPos + 6);
    doc.setFont('Roboto', 'normal');
    doc.text(String(formData.entries.length), margin + 3 * infoColWidth + 35, yPos + 6);
    yPos += 12;

    // Weather row
    doc.setFillColor(255, 255, 255);
    doc.rect(margin, yPos, pageWidth - 2 * margin, 8, 'S');
    doc.setFont('Roboto', 'bold');
    doc.text('Weather:', margin + 2, yPos + 5);
    doc.setFont('Roboto', 'normal');
    doc.text(formData.weather || '', margin + 25, yPos + 5);

    doc.setFont('Roboto', 'bold');
    doc.text('Wind speed:', margin + 70, yPos + 5);
    doc.setFont('Roboto', 'normal');
    doc.text(formData.wind_speed || '', margin + 95, yPos + 5);

    doc.setFont('Roboto', 'bold');
    doc.text('Temperature:', margin + 130, yPos + 5);
    doc.setFont('Roboto', 'normal');
    doc.text(formData.temperature || '', margin + 160, yPos + 5);

    const supervisorName = formData.omega_supervisor
      ? allPeople.find(p => p.id.toString() === formData.omega_supervisor?.toString())
      : null;
    doc.setFont('Roboto', 'bold');
    doc.text('Supervisor:', margin + 190, yPos + 5);
    doc.setFont('Roboto', 'normal');
    doc.text(supervisorName ? `${supervisorName.last_name} ${supervisorName.first_name}` : '', margin + 225, yPos + 5);
    yPos += 10;

    // Activities on site table
    doc.setFont('Roboto', 'bold');
    doc.setFontSize(10);
    doc.text('Activities on site', margin, yPos + 5);
    yPos += 7;

    // Prepare table data
    const tableHeaders = [
      ['No', 'Technicians', 'Initials', 'Tasks', 'Start', 'Stop', 'Break Time', 'Work hours', 'Admin. hours', 'Extra work', 'Waiting Weather', 'NCR Waiting']
    ];

    const tableData = formData.entries.map((entry, index) => {
      const person = allPeople.find(p => p.id?.toString() === entry.technician?.toString());
      const techName = person ? `${person.last_name} ${person.first_name}` : entry.technician_name || '';
      return [
        String(index + 1),
        techName,
        entry.initials || '',
        entry.tasks || '',
        entry.start_time || '',
        entry.end_time || '',
        (parseFloat(entry.break_time) || 0).toFixed(2),
        (parseFloat(entry.installation_hours) || 0).toFixed(2),
        (parseFloat(entry.admin_hours) || 0).toFixed(2),
        (parseFloat(entry.extra_work_hours) || 0).toFixed(2),
        (parseFloat(entry.waiting_weather_hours) || 0).toFixed(2),
        (parseFloat(entry.ncr_waiting_hours) || 0).toFixed(2)
      ];
    });

    const currentTotals = calculateTotals();
    const totalNormal = currentTotals.installation + currentTotals.admin - currentTotals.breakTime;

    // Calculate table width to match the total bar below (full page width)
    const tableWidth = pageWidth - 2 * margin;

    doc.autoTable({
      head: tableHeaders,
      body: tableData,
      startY: yPos,
      margin: { left: margin, right: margin },
      tableWidth: tableWidth,
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
        lineWidth: 0.1,
        lineColor: [200, 200, 200],
        font: 'Roboto'
      },
      headStyles: {
        fillColor: [100, 100, 100],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
        font: 'Roboto'
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },      // No
        1: { cellWidth: 40 },                         // Technicians
        2: { cellWidth: 18, halign: 'center' },      // Initials
        3: { cellWidth: 'auto' },                     // Tasks (flexible)
        4: { cellWidth: 18, halign: 'center' },      // Start
        5: { cellWidth: 18, halign: 'center' },      // Stop
        6: { cellWidth: 22, halign: 'center' },      // Break Time
        7: { cellWidth: 22, halign: 'center' },      // Work hours
        8: { cellWidth: 22, halign: 'center' },      // Admin hours
        9: { cellWidth: 22, halign: 'center' },      // Extra work
        10: { cellWidth: 26, halign: 'center' },     // Waiting Weather
        11: { cellWidth: 24, halign: 'center' }      // NCR Waiting
      },
      foot: [[
        '', 'Workers on site:', String(formData.entries.length), '', '', 'TOTAL:',
        currentTotals.breakTime.toFixed(2),
        currentTotals.installation.toFixed(2),
        currentTotals.admin.toFixed(2),
        currentTotals.extra.toFixed(2),
        currentTotals.waitingWeather.toFixed(2),
        currentTotals.ncrWaiting.toFixed(2)
      ]],
      footStyles: {
        fillColor: grayBg,
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        font: 'Roboto'
      }
    });

    yPos = doc.lastAutoTable.finalY + 2;

    // Total normal hours row
    doc.setFillColor(...greenBg);
    doc.rect(margin, yPos, pageWidth - 2 * margin, 8, 'F');
    doc.setTextColor(0, 100, 0);
    doc.setFontSize(9);
    doc.setFont('Roboto', 'bold');
    doc.text('TOTAL WORK:', margin + 5, yPos + 5.5);
    doc.setFontSize(12);
    doc.text(totalNormal.toFixed(2), pageWidth - margin - 25, yPos + 5.5);
    yPos += 12;

    // Additional works section
    if (formData.additional_works && formData.additional_works.length > 0) {
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.setFont('Roboto', 'bold');
      doc.text('Additional works', margin, yPos + 5);
      yPos += 7;

      const additionalHeaders = [['No', 'Technicians', 'Tasks and description', 'Start', 'Stop', 'Break Time', 'NCR', 'Hours']];
      const additionalData = [];

      formData.additional_works.forEach((work, index) => {
        if (work.technicians && work.technicians.length > 0) {
          // Add row for each technician
          work.technicians.forEach((tech, techIdx) => {
            const person = allPeople.find(p => p.id?.toString() === tech.technician?.toString());
            const techName = person ? `${person.last_name} ${person.first_name}` : '';
            additionalData.push([
              techIdx === 0 ? String(index + 1) : '',
              techName,
              techIdx === 0 ? (work.description || '') : '',
              tech.start_time || '',
              tech.stop_time || '',
              (parseFloat(tech.break_time) || 0).toFixed(2),
              techIdx === 0 ? (work.ncr_reference || '') : '',
              techIdx === 0 ? (parseFloat(work.hours) || 0).toFixed(2) : ''
            ]);
          });
        } else {
          // No technicians - just show the task
          additionalData.push([
            String(index + 1),
            '',
            work.description || '',
            '',
            '',
            '',
            work.ncr_reference || '',
            (parseFloat(work.hours) || 0).toFixed(2)
          ]);
        }
      });

      doc.autoTable({
        head: additionalHeaders,
        body: additionalData,
        startY: yPos,
        margin: { left: margin, right: margin },
        tableWidth: tableWidth,
        styles: {
          fontSize: 7,
          cellPadding: 1.5,
          lineWidth: 0.1,
          lineColor: [200, 200, 200],
          font: 'Roboto'
        },
        headStyles: {
          fillColor: [100, 100, 100],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          halign: 'center',
          font: 'Roboto'
        },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },     // No
          1: { cellWidth: 40 },                        // Technicians
          2: { cellWidth: 'auto' },                    // Tasks/description (flexible)
          3: { cellWidth: 18, halign: 'center' },     // Start
          4: { cellWidth: 18, halign: 'center' },     // Stop
          5: { cellWidth: 22, halign: 'center' },     // Break Time
          6: { cellWidth: 30, halign: 'center' },     // NCR
          7: { cellWidth: 22, halign: 'center' }      // Hours
        },
        foot: [['', '', '', '', '', '', 'TOTAL:', currentTotals.additionalHours.toFixed(2)]],
        footStyles: {
          fillColor: orangeBg,
          textColor: [150, 75, 0],
          fontStyle: 'bold',
          font: 'Roboto'
        }
      });

      yPos = doc.lastAutoTable.finalY + 5;
    }

    // Calculate space needed for notes and signatures
    const signaturesHeight = 35; // Height needed for signatures section
    let notesHeight = 0;
    let noteLines = [];

    if (formData.notes) {
      doc.setFontSize(8);
      noteLines = doc.splitTextToSize(formData.notes, pageWidth - 2 * margin - 10);
      notesHeight = 7 + Math.max(22, noteLines.length * 4 + 6); // header + box
    }

    const totalNeededSpace = notesHeight + signaturesHeight + 5; // 5mm padding
    const remainingSpace = pageHeight - yPos - margin;

    // Only add new page if there's not enough space for notes + signatures combined
    if (remainingSpace < totalNeededSpace) {
      doc.addPage();
      doc.setFont('Roboto', 'normal'); // Re-set font after page change
      yPos = 15;
    }

    // Notes section
    if (formData.notes) {
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.setFont('Roboto', 'bold');
      doc.text('Notes, Other', margin, yPos + 5);
      yPos += 7;

      doc.setFont('Roboto', 'normal');
      doc.setFontSize(8);
      doc.rect(margin, yPos, pageWidth - 2 * margin, Math.max(20, noteLines.length * 4 + 4), 'S');
      doc.text(noteLines, margin + 3, yPos + 5);
      yPos += Math.max(22, noteLines.length * 4 + 6);
    }

    // Signatures section (no separate page check - already calculated above)
    const sigWidth = (pageWidth - 2 * margin - 10) / 2;

    doc.setFontSize(9);
    doc.setFont('Roboto', 'bold');
    doc.text('Supervisor Signature', margin, yPos + 5);
    doc.text('Customer Representative', margin + sigWidth + 10, yPos + 5);
    yPos += 7;

    // Signature boxes
    doc.rect(margin, yPos, sigWidth, 20, 'S');
    doc.rect(margin + sigWidth + 10, yPos, sigWidth, 20, 'S');

    doc.setFont('Roboto', 'normal');
    doc.setFontSize(8);
    if (formData.omega_supervisor_signature) {
      doc.text(`Name: ${formData.omega_supervisor_signature}`, margin + 3, yPos + 8);
    }
    if (formData.omega_supervisor_signature_date) {
      doc.text(`Date: ${formData.omega_supervisor_signature_date}`, margin + 3, yPos + 14);
    }

    if (formData.customer_signature) {
      doc.text(`Name: ${formData.customer_signature}`, margin + sigWidth + 13, yPos + 8);
    }
    if (formData.customer_signature_date) {
      doc.text(`Date: ${formData.customer_signature_date}`, margin + sigWidth + 13, yPos + 14);
    }

    // Customer representative info
    if (formData.customer_representative) {
      doc.setFontSize(7);
      doc.text(`Customer Rep: ${formData.customer_representative}`, margin + sigWidth + 13, yPos + 18);
    }

    // Add page numbers if more than one page
    const totalPages = doc.internal.getNumberOfPages();
    if (totalPages > 1) {
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 20, pageHeight - 5);
      }
    }

    // Save PDF
    const fileName = `Daily_Report_${project?.project_number || 'Project'}_${format(selectedDate, 'yyyy-MM-dd')}.pdf`;
    doc.save(fileName);
  };

  const totals = calculateTotals();
  const totalNormalHours = totals.installation + totals.admin - totals.breakTime;
  const project = projects?.find(p => p.id.toString() === selectedProject);

  // Date navigation
  const handleDateChange = (direction) => {
    setSelectedDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
      return newDate;
    });
  };

  if (loadingProjects) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-steel-200 p-12">
        <div className="text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-brand-600 animate-spin" />
          <p className="text-slate-500">Loading projects...</p>
        </div>
      </div>
    );
  }

  if (!selectedProject && projects.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-steel-200 p-12">
        <div className="text-center text-slate-500">
          <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No Projects Found</h3>
          <p>Create a project first to manage daily reports</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-steel-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <FileText className="w-6 h-6 text-brand-600" />
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Daily Reports</h2>
              <p className="text-sm text-slate-500">{project?.name || 'Select a project'}</p>
            </div>
          </div>

          {/* Project Selector */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-slate-600">Project:</label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="text-sm border border-steel-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
            >
              <option value="">Select project...</option>
              {projects.map(proj => (
                <option key={proj.id} value={proj.id.toString()}>
                  {proj.project_number} - {proj.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleDateChange('prev')}
              className="p-2 hover:bg-steel-100 rounded-xl transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="relative">
              <input
                type="date"
                value={format(selectedDate, 'yyyy-MM-dd')}
                onChange={(e) => {
                  if (e.target.value) {
                    setSelectedDate(new Date(e.target.value + 'T12:00:00'));
                  }
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className="flex items-center gap-2 px-4 py-2 bg-steel-50 rounded-xl hover:bg-steel-100 cursor-pointer transition-colors">
                <Calendar className="w-4 h-4 text-slate-500" />
                <span className="font-medium">{format(selectedDate, 'dd.MM.yyyy')}</span>
              </div>
            </div>
            <button
              onClick={() => handleDateChange('next')}
              className="p-2 hover:bg-steel-100 rounded-xl transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={generateFromTasks}
              disabled={loading || !selectedProject}
              className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 transition-colors disabled:opacity-50"
              title="Generate from daily task assignments"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              From Tasks
            </button>
            <button
              onClick={generateFromTimesheets}
              disabled={loading || !selectedProject}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50"
              title="Generate from timesheets (tasks will be empty)"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
              From Timesheets
            </button>
          </div>
        </div>
      </div>

      {/* Reports List */}
      {!showReportForm && !selectedProject && (
        <div className="bg-white rounded-xl shadow-sm border border-steel-200 p-8">
          <div className="text-center text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>Select a project to view and manage daily reports</p>
          </div>
        </div>
      )}

      {!showReportForm && selectedProject && (
        <div className="space-y-4">
          {/* Statistics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl shadow-sm border border-steel-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5 text-brand-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{reportStats.total}</div>
                  <div className="text-xs text-slate-500">Total Reports</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-steel-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{reportStats.totalHours.toFixed(0)}</div>
                  <div className="text-xs text-slate-500">Total Hours</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-steel-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{reportStats.totalWorkers}</div>
                  <div className="text-xs text-slate-500">Worker Days</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-steel-200 p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{reportStats.total > 0 ? (reportStats.totalHours / reportStats.total).toFixed(1) : 0}</div>
                  <div className="text-xs text-slate-500">Avg Hours/Day</div>
                </div>
              </div>
            </div>
          </div>

          {/* Reports List with Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-steel-200">
            {/* Header with Filters Toggle */}
            <div className="p-4 border-b border-steel-200">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-slate-900 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-slate-500" />
                  Reports List
                  <span className="text-sm font-normal text-slate-500">
                    ({filteredReports.length} of {reports.length})
                  </span>
                </h3>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium transition-colors ${
                    showFilters ? 'bg-brand-100 text-brand-700' : 'bg-steel-100 text-slate-600 hover:bg-steel-200'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filters
                </button>
              </div>

              {/* Filters Panel */}
              {showFilters && (
                <div className="mt-4 p-4 bg-steel-50 rounded-xl border border-steel-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {/* Search */}
                    <div className="lg:col-span-2">
                      <label className="block text-xs font-medium text-slate-500 mb-1">Search</label>
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search by date, notes, supervisor..."
                          className="w-full pl-10 pr-3 py-2 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Status Filter */}
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="all">All Statuses</option>
                        <option value="draft">Draft</option>
                        <option value="pending_approval">Pending Approval</option>
                        <option value="approved">Approved</option>
                      </select>
                    </div>

                    {/* Date From */}
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Date From</label>
                      <input
                        type="date"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Date To */}
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Date To</label>
                      <input
                        type="date"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-steel-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Clear Filters */}
                  {(filterStatus !== 'all' || filterDateFrom || filterDateTo || searchQuery) && (
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        {filterStatus !== 'all' && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-brand-100 text-brand-700 rounded-full text-xs">
                            Status: {filterStatus}
                            <button onClick={() => setFilterStatus('all')} className="hover:text-brand-900">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        )}
                        {filterDateFrom && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-brand-100 text-brand-700 rounded-full text-xs">
                            From: {filterDateFrom}
                            <button onClick={() => setFilterDateFrom('')} className="hover:text-brand-900">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        )}
                        {filterDateTo && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-brand-100 text-brand-700 rounded-full text-xs">
                            To: {filterDateTo}
                            <button onClick={() => setFilterDateTo('')} className="hover:text-brand-900">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        )}
                        {searchQuery && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-brand-100 text-brand-700 rounded-full text-xs">
                            Search: "{searchQuery}"
                            <button onClick={() => setSearchQuery('')} className="hover:text-brand-900">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        )}
                      </div>
                      <button
                        onClick={clearFilters}
                        className="text-sm text-slate-500 hover:text-slate-700"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Table Header with Sort */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-steel-50 border-b border-steel-200 text-xs font-medium text-slate-500 uppercase tracking-wider">
              <button
                onClick={() => toggleSort('date')}
                className="col-span-2 flex items-center gap-1 hover:text-slate-700"
              >
                Date
                {sortField === 'date' && (sortDirection === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />)}
              </button>
              <div className="col-span-2">Supervisor</div>
              <button
                onClick={() => toggleSort('workers')}
                className="col-span-1 flex items-center gap-1 hover:text-slate-700"
              >
                Workers
                {sortField === 'workers' && (sortDirection === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />)}
              </button>
              <button
                onClick={() => toggleSort('hours')}
                className="col-span-2 flex items-center gap-1 hover:text-slate-700"
              >
                Hours
                {sortField === 'hours' && (sortDirection === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />)}
              </button>
              <button
                onClick={() => toggleSort('status')}
                className="col-span-2 flex items-center gap-1 hover:text-slate-700"
              >
                Status
                {sortField === 'status' && (sortDirection === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />)}
              </button>
              <div className="col-span-3 text-right">Actions</div>
            </div>

            {/* Reports List */}
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-brand-600" />
              </div>
            ) : filteredReports.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>{reports.length === 0 ? 'No reports found for this project' : 'No reports match your filters'}</p>
                {reports.length > 0 && (
                  <button
                    onClick={clearFilters}
                    className="mt-2 text-brand-600 hover:text-brand-700 text-sm"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredReports.map(report => {
                  const supervisor = report.omega_supervisor
                    ? allPeople.find(p => p.id === report.omega_supervisor)
                    : null;
                  return (
                    <div
                      key={report.id}
                      className="p-4 hover:bg-steel-50 transition-colors"
                    >
                      <div className="md:grid md:grid-cols-12 md:gap-4 md:items-center">
                        {/* Date */}
                        <div className="col-span-2 flex items-center gap-3 mb-2 md:mb-0">
                          <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-brand-600" />
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">
                              {format(parseISO(report.date), 'dd.MM.yyyy')}
                            </div>
                            <div className="text-xs text-slate-500 md:hidden">
                              {format(parseISO(report.date), 'EEEE')}
                            </div>
                          </div>
                        </div>

                        {/* Supervisor */}
                        <div className="col-span-2 text-sm text-slate-600 mb-2 md:mb-0">
                          {supervisor ? `${supervisor.first_name} ${supervisor.last_name}` : '-'}
                        </div>

                        {/* Workers */}
                        <div className="col-span-1 mb-2 md:mb-0">
                          <span className="inline-flex items-center gap-1 text-sm">
                            <Users className="w-4 h-4 text-slate-400" />
                            {report.workers_on_site || 0}
                          </span>
                        </div>

                        {/* Hours */}
                        <div className="col-span-2 mb-2 md:mb-0">
                          <div className="text-sm font-medium text-slate-900">
                            {report.total_normal_hours?.toFixed(2) || '0.00'}h
                          </div>
                          {report.additional_hours > 0 && (
                            <div className="text-xs text-orange-600">
                              +{report.additional_hours?.toFixed(2)}h extra
                            </div>
                          )}
                        </div>

                        {/* Status */}
                        <div className="col-span-2 mb-2 md:mb-0">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                            report.status === 'approved' ? 'bg-green-100 text-green-700' :
                            report.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-steel-100 text-slate-700'
                          }`}>
                            {report.status === 'approved' && <CheckCircle className="w-3 h-3" />}
                            {report.status === 'pending_approval' && <AlertCircle className="w-3 h-3" />}
                            {report.status === 'draft' && <FileEdit className="w-3 h-3" />}
                            {report.status === 'approved' ? 'Approved' :
                             report.status === 'pending_approval' ? 'Pending' : 'Draft'}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="col-span-3 flex items-center justify-end gap-1">
                          <button
                            onClick={() => viewReport(report)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-brand-50 text-brand-600 rounded-xl hover:bg-brand-100 transition-colors"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                            <span className="hidden lg:inline">View</span>
                          </button>
                          <button
                            onClick={() => loadReport(report)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-steel-50 text-slate-600 rounded-xl hover:bg-steel-100 transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                            <span className="hidden lg:inline">Edit</span>
                          </button>
                          <button
                            onClick={() => {
                              setSelectedDate(parseISO(report.date));
                              exportToPDF();
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-600 rounded-xl hover:bg-green-100 transition-colors"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                            <span className="hidden lg:inline">PDF</span>
                          </button>
                          <button
                            onClick={() => deleteReport(report.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Notes preview */}
                      {report.notes && (
                        <div className="mt-2 ml-13 md:ml-0 md:pl-13 text-xs text-slate-500 truncate max-w-xl">
                          <span className="font-medium">Notes:</span> {report.notes}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Report Form */}
      {showReportForm && (
        <div className="bg-white rounded-xl shadow-sm border border-steel-200 overflow-hidden">
          {/* Form Header */}
          <div className={`bg-gradient-to-r ${viewOnly ? 'from-blue-700 to-blue-800' : 'from-slate-700 to-slate-800'} text-white p-4`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div>
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    {viewOnly && <Eye className="w-5 h-5" />}
                    Daily Report Sheet {viewOnly && '(View Only)'}
                  </h3>
                  <p className="text-sm text-white/70">
                    {project?.project_number} - {project?.name}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {viewOnly && (
                  <button
                    onClick={() => setViewOnly(false)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-xl transition-colors text-sm"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </button>
                )}
                <button
                  onClick={() => setShowReportForm(false)}
                  className="p-2 hover:bg-white/10 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Report Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-steel-50 border-b border-steel-200">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Date</label>
              <div className="flex items-center gap-2 text-sm font-medium">
                <Calendar className="w-4 h-4 text-slate-400" />
                {format(selectedDate, 'dd.MM.yyyy')}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Weather</label>
              <div className="flex items-center gap-1">
                <Cloud className="w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={formData.weather || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, weather: e.target.value }))}
                  placeholder="Cloudy"
                  disabled={viewOnly}
                  className={`flex-1 text-sm border border-steel-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${viewOnly ? 'bg-steel-100 cursor-not-allowed' : ''}`}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Wind Speed</label>
              <div className="flex items-center gap-1">
                <Wind className="w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={formData.wind_speed || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, wind_speed: e.target.value }))}
                  placeholder="12 m/s"
                  disabled={viewOnly}
                  className={`flex-1 text-sm border border-steel-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${viewOnly ? 'bg-steel-100 cursor-not-allowed' : ''}`}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Temperature</label>
              <div className="flex items-center gap-1">
                <Thermometer className="w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={formData.temperature || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, temperature: e.target.value }))}
                  placeholder="10 C"
                  disabled={viewOnly}
                  className={`flex-1 text-sm border border-steel-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${viewOnly ? 'bg-steel-100 cursor-not-allowed' : ''}`}
                />
              </div>
            </div>
          </div>

          {/* Supervisor Info */}
          <div className="grid grid-cols-2 gap-4 p-4 border-b border-steel-200">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Omega Wind Supervisor</label>
              <select
                value={formData.omega_supervisor || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, omega_supervisor: e.target.value || null }))}
                className="w-full text-sm border border-steel-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select supervisor...</option>
                {allPeople.map(person => (
                  <option key={person.id} value={person.id}>
                    {person.last_name} {person.first_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Customer Representative</label>
              <input
                type="text"
                value={formData.customer_representative}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_representative: e.target.value }))}
                placeholder="e.g., Laurentiu Ruster"
                className="w-full text-sm border border-steel-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Activities Table */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-slate-900 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Activities on site
              </h4>
              {!viewOnly && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={recalculateAllWorkHours}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-600 rounded-xl hover:bg-green-100 transition-colors"
                    title="Recalculate Work hours for all entries based on Start/Stop/Break"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Recalculate
                  </button>
                  <button
                    onClick={addEntry}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-brand-50 text-brand-600 rounded-xl hover:bg-brand-100 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Row
                  </button>
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-steel-100">
                    <th className="px-2 py-2 text-left font-medium text-slate-600 w-8">No</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Technicians</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600 w-16">Initials</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Tasks</th>
                    <th className="px-2 py-2 text-center font-medium text-slate-600 w-20">Start</th>
                    <th className="px-2 py-2 text-center font-medium text-slate-600 w-20">Stop</th>
                    <th className="px-2 py-2 text-center font-medium text-slate-600 w-20">Break</th>
                    <th className="px-2 py-2 text-center font-medium text-slate-600 w-20">Work</th>
                    <th className="px-2 py-2 text-center font-medium text-slate-600 w-20">Admin.</th>
                    <th className="px-2 py-2 text-center font-medium text-slate-600 w-20">Extra</th>
                    <th className="px-2 py-2 text-center font-medium text-slate-600 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.entries.map((entry, index) => (
                    <tr key={index} className="border-b border-steel-100 hover:bg-steel-50">
                      <td className="px-2 py-1.5 text-slate-500">{index + 1}</td>
                      <td className="px-2 py-1.5">
                        <select
                          value={entry.technician || ''}
                          onChange={(e) => {
                            const person = allPeople.find(p => p.id.toString() === e.target.value);
                            updateEntry(index, 'technician', e.target.value);
                            if (person) {
                              updateEntry(index, 'technician_name', `${person.last_name} ${person.first_name}`);
                              updateEntry(index, 'initials', (person.first_name?.slice(0,2) + person.last_name?.slice(0,2)).toUpperCase());
                            }
                          }}
                          className="w-full text-xs border border-steel-300 rounded px-1 py-1 focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Select...</option>
                          {allPeople.map(person => (
                            <option key={person.id} value={person.id}>
                              {person.last_name} {person.first_name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          value={entry.initials}
                          onChange={(e) => updateEntry(index, 'initials', e.target.value)}
                          className="w-full text-xs border border-steel-300 rounded px-1 py-1 text-center focus:ring-1 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          value={entry.tasks}
                          onChange={(e) => updateEntry(index, 'tasks', e.target.value)}
                          className="w-full text-xs border border-steel-300 rounded px-1 py-1 focus:ring-1 focus:ring-blue-500"
                          placeholder="Task description..."
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="time"
                          value={entry.start_time}
                          onChange={(e) => updateEntry(index, 'start_time', e.target.value)}
                          className="w-full text-xs border border-steel-300 rounded px-1 py-1 text-center focus:ring-1 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="time"
                          value={entry.end_time}
                          onChange={(e) => updateEntry(index, 'end_time', e.target.value)}
                          className="w-full text-xs border border-steel-300 rounded px-1 py-1 text-center focus:ring-1 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          step="0.25"
                          value={entry.break_time}
                          onChange={(e) => updateEntry(index, 'break_time', e.target.value)}
                          className="w-full text-xs border border-steel-300 rounded px-1 py-1 text-center focus:ring-1 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          step="0.25"
                          value={entry.installation_hours}
                          onChange={(e) => updateEntry(index, 'installation_hours', e.target.value)}
                          className="w-full text-xs border border-steel-300 rounded px-1 py-1 text-center focus:ring-1 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          step="0.25"
                          value={entry.admin_hours}
                          onChange={(e) => updateEntry(index, 'admin_hours', e.target.value)}
                          className="w-full text-xs border border-steel-300 rounded px-1 py-1 text-center focus:ring-1 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          step="0.25"
                          value={entry.extra_work_hours}
                          onChange={(e) => updateEntry(index, 'extra_work_hours', e.target.value)}
                          className="w-full text-xs border border-steel-300 rounded px-1 py-1 text-center focus:ring-1 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <button
                          onClick={() => removeEntry(index)}
                          className="p-1 hover:bg-red-50 rounded text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-steel-100 font-medium">
                    <td colSpan="2" className="px-2 py-2 text-right">Workers on site:</td>
                    <td className="px-2 py-2 text-center">{formData.entries.length}</td>
                    <td colSpan="3" className="px-2 py-2 text-right">TOTAL:</td>
                    <td className="px-2 py-2 text-center text-brand-600">{totals.breakTime.toFixed(2)}</td>
                    <td className="px-2 py-2 text-center text-brand-600">{totals.installation.toFixed(2)}</td>
                    <td className="px-2 py-2 text-center text-brand-600">{totals.admin.toFixed(2)}</td>
                    <td className="px-2 py-2 text-center text-brand-600">{totals.extra.toFixed(2)}</td>
                    <td></td>
                  </tr>
                  <tr className="bg-green-50">
                    <td colSpan="7" className="px-2 py-2 text-right font-medium text-green-800">
                      TOTAL WORK:
                    </td>
                    <td colSpan="4" className="px-2 py-2 text-center font-bold text-green-700 text-lg">
                      {totalNormalHours.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Additional Works */}
          <div className="p-4 border-t border-steel-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-slate-900">Additional works</h4>
              {!viewOnly && (
                <button
                  onClick={addAdditionalWork}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-50 text-orange-600 rounded-xl hover:bg-orange-100 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Work
                </button>
              )}
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="bg-steel-100">
                  <th className="px-2 py-2 text-left font-medium text-slate-600 w-8">No</th>
                  <th className="px-2 py-2 text-left font-medium text-slate-600">Tasks and description of NCR's, standbys</th>
                  <th className="px-2 py-2 text-center font-medium text-slate-600 w-24">NCR</th>
                  <th className="px-2 py-2 text-center font-medium text-slate-600 w-24">Hours</th>
                  <th className="px-2 py-2 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {formData.additional_works.map((work, index) => (
                  <React.Fragment key={index}>
                    <tr className="border-b border-steel-100">
                      <td className="px-2 py-1.5 text-slate-500">{index + 1}</td>
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          value={work.description}
                          onChange={(e) => updateAdditionalWork(index, 'description', e.target.value)}
                          className="w-full text-xs border border-steel-300 rounded px-2 py-1 focus:ring-1 focus:ring-blue-500"
                          placeholder="e.g., Extra work - Painting tower flanges ANMA, MIMU WTG04 2prs x 2H"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="text"
                          value={work.ncr_reference}
                          onChange={(e) => updateAdditionalWork(index, 'ncr_reference', e.target.value)}
                          className="w-full text-xs border border-steel-300 rounded px-2 py-1 text-center focus:ring-1 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="number"
                          step="0.25"
                          value={work.hours}
                          onChange={(e) => updateAdditionalWork(index, 'hours', e.target.value)}
                          className="w-full text-xs border border-steel-300 rounded px-2 py-1 text-center focus:ring-1 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <button
                          onClick={() => removeAdditionalWork(index)}
                          className="p-1 hover:bg-red-50 rounded text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                    {/* Technicians for this additional work */}
                    <tr className="bg-brand-50/30">
                      <td></td>
                      <td colSpan="4" className="px-2 py-2">
                        <div className="ml-4 border-l-2 border-brand-300 pl-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-brand-700">Technicians assigned to this task</span>
                            <button
                              onClick={() => addAdditionalWorkTechnician(index)}
                              className="text-xs px-2 py-1 bg-brand-500 text-white rounded hover:bg-brand-600 flex items-center gap-1"
                            >
                              <Plus className="w-3 h-3" /> Add Person
                            </button>
                          </div>
                          {(work.technicians || []).length > 0 && (
                            <div className="space-y-1">
                              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-slate-500 mb-1">
                                <div className="col-span-4">Technician</div>
                                <div className="col-span-2 text-center">Start</div>
                                <div className="col-span-2 text-center">Stop</div>
                                <div className="col-span-2 text-center">Break</div>
                                <div className="col-span-2"></div>
                              </div>
                              {work.technicians.map((tech, techIndex) => (
                                <div key={techIndex} className="grid grid-cols-12 gap-2 items-center">
                                  <div className="col-span-4">
                                    <select
                                      value={tech.technician || ''}
                                      onChange={(e) => updateAdditionalWorkTechnician(index, techIndex, 'technician', e.target.value)}
                                      className="w-full text-xs border border-steel-300 rounded px-2 py-1 focus:ring-1 focus:ring-blue-500"
                                    >
                                      <option value="">Select technician...</option>
                                      {formData.entries
                                        .filter(e => e.technician)
                                        .map(e => allPeople.find(p => p.id?.toString() === e.technician?.toString()))
                                        .filter(Boolean)
                                        .filter((person, idx, arr) => arr.findIndex(p => p.id === person.id) === idx)
                                        .map(person => (
                                          <option key={person.id} value={person.id}>
                                            {person.first_name} {person.last_name}
                                          </option>
                                        ))}
                                    </select>
                                  </div>
                                  <div className="col-span-2">
                                    <input
                                      type="time"
                                      value={tech.start_time || ''}
                                      onChange={(e) => updateAdditionalWorkTechnician(index, techIndex, 'start_time', e.target.value)}
                                      className="w-full text-xs border border-steel-300 rounded px-1 py-1 focus:ring-1 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div className="col-span-2">
                                    <input
                                      type="time"
                                      value={tech.stop_time || ''}
                                      onChange={(e) => updateAdditionalWorkTechnician(index, techIndex, 'stop_time', e.target.value)}
                                      className="w-full text-xs border border-steel-300 rounded px-1 py-1 focus:ring-1 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div className="col-span-2">
                                    <input
                                      type="number"
                                      step="0.25"
                                      value={tech.break_time || 0}
                                      onChange={(e) => updateAdditionalWorkTechnician(index, techIndex, 'break_time', e.target.value)}
                                      className="w-full text-xs border border-steel-300 rounded px-1 py-1 text-center focus:ring-1 focus:ring-blue-500"
                                      placeholder="0"
                                    />
                                  </div>
                                  <div className="col-span-2 flex justify-center">
                                    <button
                                      onClick={() => removeAdditionalWorkTechnician(index, techIndex)}
                                      className="p-1 hover:bg-red-100 rounded text-red-500"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-orange-50">
                  <td colSpan="3" className="px-2 py-2 text-right font-medium text-orange-800">TOTAL:</td>
                  <td className="px-2 py-2 text-center font-bold text-orange-700">{totals.additionalHours.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Notes */}
          <div className="p-4 border-t border-steel-200">
            <h4 className="font-medium text-slate-900 mb-2">Notes, Other</h4>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={4}
              className="w-full text-sm border border-steel-300 rounded-xl px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Crane MC1 WTG04 -> WTG10&#10;Crane relocation&#10;&#10;WTG04 Punch list&#10;Observation 314: Factory issue..."
            />
          </div>

          {/* Signatures */}
          <div className="grid grid-cols-2 gap-4 p-4 border-t border-steel-200 bg-steel-50">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Supervisor Signature</label>
              <input
                type="text"
                value={formData.omega_supervisor_signature}
                onChange={(e) => setFormData(prev => ({ ...prev, omega_supervisor_signature: e.target.value }))}
                className="w-full text-sm border border-steel-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-blue-500"
                placeholder="Name"
              />
              <input
                type="date"
                value={formData.omega_supervisor_signature_date}
                onChange={(e) => setFormData(prev => ({ ...prev, omega_supervisor_signature_date: e.target.value }))}
                className="w-full mt-1 text-sm border border-steel-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Customer Representative Signature</label>
              <input
                type="text"
                value={formData.customer_signature}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_signature: e.target.value }))}
                className="w-full text-sm border border-steel-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-blue-500"
                placeholder="Name"
              />
              <input
                type="date"
                value={formData.customer_signature_date}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_signature_date: e.target.value }))}
                className="w-full mt-1 text-sm border border-steel-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between p-4 border-t border-steel-200 bg-white">
            <div className="flex items-center gap-2">
              <select
                value={formData.status || 'draft'}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                disabled={viewOnly}
                className={`text-sm border border-steel-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 ${viewOnly ? 'bg-steel-100 cursor-not-allowed' : ''}`}
              >
                <option value="draft">Draft</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="approved">Approved</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowReportForm(false)}
                className="px-4 py-2 text-slate-600 hover:bg-steel-100 rounded-xl transition-colors"
              >
                {viewOnly ? 'Close' : 'Cancel'}
              </button>
              {!viewOnly && (
                <button
                  onClick={saveReport}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 transition-colors disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Report
                </button>
              )}
              <button
                onClick={exportToPDF}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Export PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyReport;
